import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, MessageSquare, Info, CreditCard,
  Wrench, HelpCircle, FileText, ToggleLeft, ToggleRight, Bot
} from "lucide-react";

const API = `${window.location.origin}/api`;

const CONTENT_TYPES = [
  { key: "welcome",          label: "Karşılama Mesajı",     icon: MessageSquare, color: "text-green-600",  bg: "bg-green-50",  border: "border-green-200" },
  { key: "general_info",     label: "Genel Bilgiler",        icon: Info,          color: "text-blue-600",   bg: "bg-blue-50",   border: "border-blue-200" },
  { key: "installment_info", label: "Taksit Bilgileri",      icon: CreditCard,    color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
  { key: "service_info",     label: "Servis Bilgileri",      icon: Wrench,        color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
  { key: "faq",              label: "Sıkça Sorulan Sorular", icon: HelpCircle,    color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200" },
  { key: "custom_note",      label: "Özel Bilgi Notları",    icon: FileText,      color: "text-red-600",    bg: "bg-red-50",    border: "border-red-200" },
];

const EMPTY_FORM = { content_type: "general_info", title: "", content: "", active: true, order: 0 };

function getTypeInfo(key) {
  return CONTENT_TYPES.find(t => t.key === key) || CONTENT_TYPES[1];
}

function ContentModal({ item, onSave, onClose }) {
  const [form, setForm] = useState(item ? {
    content_type: item.content_type,
    title: item.title,
    content: item.content,
    active: item.active,
    order: item.order ?? 0,
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const handle = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Başlık ve içerik zorunludur");
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, order: parseInt(form.order) || 0 });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-[#DC2626]" />
            <h3 className="text-lg font-bold uppercase text-gray-900"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              {item ? "İçerik Düzenle" : "Yeni İçerik Ekle"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Type selector */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              İçerik Tipi
            </label>
            <select
              data-testid="content-type-select"
              value={form.content_type}
              onChange={handle("content_type")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] bg-white"
            >
              {CONTENT_TYPES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              Başlık *
            </label>
            <input
              data-testid="content-title-input"
              type="text"
              value={form.title}
              onChange={handle("title")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              placeholder="Örn: Garanti süresi ne kadar?"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              İçerik *
            </label>
            <textarea
              data-testid="content-text-input"
              value={form.content}
              onChange={handle("content")}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] resize-y"
              placeholder="Bot'un müşteriye söyleyeceği içerik..."
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              Yapay zeka bu içeriği müşteri sorularına yanıt verirken kullanacak.
            </p>
          </div>

          {/* Order */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
              Sıra
            </label>
            <input
              data-testid="content-order-input"
              type="number"
              value={form.order}
              onChange={handle("order")}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
            />
          </div>

          {/* Active */}
          <div className="flex items-center gap-3 py-2">
            <label className="text-xs font-bold uppercase text-gray-500 tracking-wider">
              Durum
            </label>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, active: !f.active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.active ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                form.active ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
            <span className="text-sm text-gray-600">{form.active ? "Aktif" : "Pasif"}</span>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              data-testid="content-save-btn"
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ChatbotContentPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chatbot-content`, { headers });
      setItems(res.data);
    } catch { toast.error("İçerikler yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchContent(); }, []); // eslint-disable-line

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/chatbot-content`, data, { headers });
      toast.success("İçerik eklendi");
      setShowModal(false);
      fetchContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ekleme başarısız");
      throw err;
    }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${API}/chatbot-content/${editItem.id}`, data, { headers });
      toast.success("İçerik güncellendi");
      setEditItem(null);
      fetchContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncelleme başarısız");
      throw err;
    }
  };

  const handleToggle = async (id) => {
    setActionId(id);
    try {
      await axios.put(`${API}/chatbot-content/${id}/toggle`, {}, { headers });
      setItems(prev => prev.map(i => i.id === id ? { ...i, active: !i.active } : i));
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem başarısız");
    } finally { setActionId(null); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/chatbot-content/${deleteId}`, { headers });
      toast.success("İçerik silindi");
      setDeleteId(null);
      fetchContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    } finally { setDeleting(false); }
  };

  // Counts per type
  const counts = {};
  items.forEach(i => { counts[i.content_type] = (counts[i.content_type] || 0) + 1; });

  // Filtered items
  const filtered = activeFilter === "all" ? items : items.filter(i => i.content_type === activeFilter);

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-[#111827] rounded-md flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <h1 className="text-4xl font-bold uppercase text-gray-900 tracking-tight"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Chatbot İçerik Yönetimi
              </h1>
            </div>
            <p className="text-sm text-gray-500 mt-1 ml-12">
              Bot'un müşterilere verdiği bilgileri buradan yönetin
            </p>
          </div>
          <button
            data-testid="add-content-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold uppercase tracking-wide rounded-md transition-all active:scale-95"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            <Plus className="w-4 h-4" /> İçerik Ekle
          </button>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {CONTENT_TYPES.map(type => {
            const Icon = type.icon;
            const count = counts[type.key] || 0;
            return (
              <button
                key={type.key}
                data-testid={`filter-type-${type.key}`}
                onClick={() => setActiveFilter(activeFilter === type.key ? "all" : type.key)}
                className={`text-left p-3 rounded-lg border transition-all ${
                  activeFilter === type.key
                    ? `${type.bg} ${type.border} border-2`
                    : "bg-white border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${type.bg}`}>
                  <Icon className={`w-4 h-4 ${type.color}`} strokeWidth={1.5} />
                </div>
                <p className="text-xs font-bold text-gray-700 leading-tight">{type.label}</p>
                <p className={`text-lg font-bold ${type.color}`} style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {count} kayıt
                </p>
              </button>
            );
          })}
        </div>

        {/* Filter hint */}
        {activeFilter !== "all" && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-600">
              Filtre: <strong>{getTypeInfo(activeFilter).label}</strong>
            </span>
            <button onClick={() => setActiveFilter("all")}
              className="text-xs text-[#DC2626] hover:underline">Temizle</button>
          </div>
        )}

        {/* Content list */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 bg-white rounded-lg border border-gray-200">
            <Bot className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Bu kategoride henüz içerik yok.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="content-list">
            {filtered.map(item => {
              const typeInfo = getTypeInfo(item.content_type);
              const Icon = typeInfo.icon;
              return (
                <div
                  key={item.id}
                  data-testid={`content-item-${item.id}`}
                  className={`bg-white rounded-lg border border-gray-200 p-4 transition-all hover:shadow-sm ${!item.active ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${typeInfo.bg}`}>
                      <Icon className={`w-4 h-4 ${typeInfo.color}`} strokeWidth={1.5} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{item.title}</span>
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${typeInfo.bg} ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        {!item.active && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm">Pasif</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-3">{item.content}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Toggle */}
                      <button
                        data-testid={`toggle-content-btn-${item.id}`}
                        onClick={() => handleToggle(item.id)}
                        disabled={actionId === item.id}
                        className="p-1.5 text-gray-400 hover:text-gray-700 rounded-md transition-colors"
                        title={item.active ? "Devre Dışı Bırak" : "Aktifleştir"}
                      >
                        {item.active
                          ? <ToggleRight className="w-5 h-5 text-green-500" />
                          : <ToggleLeft className="w-5 h-5 text-gray-400" />
                        }
                      </button>

                      {/* Edit */}
                      <button
                        data-testid={`edit-content-btn-${item.id}`}
                        onClick={() => setEditItem(item)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Düzenle"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete */}
                      <button
                        data-testid={`delete-content-btn-${item.id}`}
                        onClick={() => setDeleteId(item.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modals */}
        {showModal && <ContentModal onSave={handleCreate} onClose={() => setShowModal(false)} />}
        {editItem && <ContentModal item={editItem} onSave={handleEdit} onClose={() => setEditItem(null)} />}

        {/* Delete confirm */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>İçeriği Sil?</h3>
              <p className="text-sm text-gray-600 mb-5">
                Bu içerik kalıcı olarak silinecek. Bot bu bilgiyi artık kullanamayacak.
              </p>
              <div className="flex gap-3">
                <button data-testid="confirm-delete-content-btn"
                  onClick={handleDelete} disabled={deleting}
                  className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50">
                  {deleting ? "..." : "Sil"}
                </button>
                <button onClick={() => setDeleteId(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
